/**
 * Tenant Detection Utilities
 *
 * Provides subdomain extraction and validation for multi-tenant routing.
 * Used by router loader/beforeLoad hooks to detect tenant from hostname.
 *
 * @module lib/router/tenant-detection
 */

/**
 * Validation result for subdomain validation
 */
export interface ValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Reserved subdomains that are not allowed for tenant access
 * These redirect to the main domain or display an error
 */
const RESERVED_SUBDOMAINS = [
  'www',       // Standard web prefix
  'sign',      // Root domain name
  'api',       // Backend API domain
  'admin',     // Administrative domain
  'app',       // Generic app domain
  'staging',   // Staging environment
  'dev',       // Development environment
  'demo',      // Demo environment (reserved for default localhost)
] as const

/**
 * Extracts subdomain from hostname
 *
 * Examples:
 * - localhost → demo
 * - localhost:5173 → demo
 * - 127.0.0.1 → demo
 * - demo.sign.app → demo
 * - acme.sign.app → acme
 * - sign.app → (empty string, root domain)
 * - www.sign.app → www (will be validated as invalid)
 *
 * @param hostname - The hostname from window.location.hostname
 * @returns The extracted subdomain, or empty string if root domain
 */
export function extractSubdomainFromHostname(hostname: string): string {
  // Remove port first if present (e.g., "example.com:3000" → "example.com", "127.0.0.1:5173" → "127.0.0.1")
  const cleanHostname = hostname.split(':')[0]

  // Handle localhost - always return 'monutrack' for local development
  if (cleanHostname === 'localhost' || cleanHostname === '127.0.0.1' || cleanHostname.includes('localhost')) {
    return 'monutrack.app'
  }

  // Split by dots
  const parts = cleanHostname.split('.')

  // If only one part (e.g., "localhost") or two parts (e.g., "sign.app"), no subdomain
  // Root domain examples: sign.app, example.com
  if (parts.length <= 2) {
    return '' // Root domain, no tenant subdomain
  }

  // First part is subdomain
  // Example: "demo.sign.app" → ["demo", "sign", "app"] → "demo"
  return parts[0]
}

/**
 * Validates subdomain against reserved names and format rules
 *
 * Validation rules:
 * - Must not be empty (root domain access not allowed)
 * - Must not be in reserved list
 * - Must contain only alphanumeric characters and hyphens
 * - Cannot start or end with hyphen
 * - Cannot contain underscores, spaces, or special characters
 *
 * @param subdomain - The subdomain to validate
 * @returns Validation result with isValid flag and optional error message
 */
export function validateSubdomain(subdomain: string): ValidationResult {
  // Empty subdomain (root domain) is invalid for tenant access
  if (!subdomain || subdomain === '') {
    return {
      isValid: false,
      error: 'Root domain access not allowed. Please use a tenant subdomain.',
    }
  }

  // Check reserved subdomains (case-insensitive)
  const lowerSubdomain = subdomain.toLowerCase()
  if (RESERVED_SUBDOMAINS.includes(lowerSubdomain as typeof RESERVED_SUBDOMAINS[number])) {
    return {
      isValid: false,
      error: `Subdomain '${subdomain}' is reserved and cannot be used for tenant access.`,
    }
  }

  // Format validation: alphanumeric and hyphens only, no underscores or special chars
  // Must not start or end with hyphen
  const validFormat = /^[a-z0-9]+(-[a-z0-9]+)*$/i
  if (!validFormat.test(subdomain)) {
    return {
      isValid: false,
      error: `Subdomain '${subdomain}' contains invalid characters. Only alphanumeric characters and hyphens are allowed.`,
    }
  }

  // Valid subdomain
  return { isValid: true }
}

/**
 * Gets the effective tenant domain for API calls
 *
 * For localhost: returns VITE_TENANT_DOMAIN environment variable
 * For production: returns actual hostname
 *
 * @param hostname - The hostname from window.location.hostname
 * @returns The effective tenant domain to use for API calls
 */
export function getEffectiveTenantDomain(hostname: string): string {
  const isLocalhost = hostname === 'localhost' || hostname.includes('localhost') || hostname === '127.0.0.1'

  if (isLocalhost) {
    // For localhost development, use VITE_TENANT_DOMAIN
    const envTenantDomain = import.meta.env.VITE_TENANT_DOMAIN
    if (!envTenantDomain) {
      throw new Error('VITE_TENANT_DOMAIN is required for localhost development')
    }
    return envTenantDomain
  }

  // For production, use the actual hostname
  return hostname
}

/**
 * Router context data shape for tenant detection
 */
export interface TenantRouterContext {
  subdomain: string
  tenantDomain: string
}
